import AlertBox from "@components/AlertBox/AlertBox";
import Checkbox from "@components/Checkbox/Checkbox";
import Header from "@components/Header/Header";
import PrimaryButton from "@components/PrimaryButton/PrimaryButton";
import RecoveryPhrase from "@components/RecoveryPhrase/RecoveryPhrase";
import { useState } from "react";
import CroppedCircleBackground from '@assets/images/CircleBackground.png';
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import {
    Container,
    RecoveryPhraseContainer,
    BackgroundImageContainer,
    Divider,
    ButtonContainer,
    ImportantNotesContainer,
    CircleBackgroundContainer,
    Title,
    NotesContainer,
    NoteItem,
    IconContainer,
    NoteText,
    CheckboxContainer
} from './ShowRecoveryPhrase.styles';
import useRecoveryPhrase from "@hooks/useRecoveryPhrase";
import LoadingModal from "@components/LoadingModal/LoadingModal";

const ShowRecoveryPhrase = () => {
    const [isAgreed, setIsAgreed] = useState(false)
    const navigate = useNavigate()
    const { t } = useTranslation()

    return (
        <Container>
            <Header title={t('recoveryPhrase.header')} showBackButton onBackClick={() => navigate('/settings')}/>
            {!isAgreed && <ImportantNotes onAgree={() => setIsAgreed(true)} />}
            {isAgreed && <RecoveryPhraseDisplay />}
        </Container>
    );
};

const RecoveryPhraseDisplay = () => {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const recoveryPhrase = useRecoveryPhrase()
    return (
        <>
            <LoadingModal visible={!recoveryPhrase} />
            <RecoveryPhraseContainer>
                <BackgroundImageContainer $backgroundImage={CroppedCircleBackground}>
                    <RecoveryPhrase 
                        mode="display"
                        phrases={recoveryPhrase.split(' ')}
                    />
                    <AlertBox 
                        alertText={t('recoveryPhrase.display.alertText')}
                        enableIcon={false}
                    />
                    <Divider />
                </BackgroundImageContainer>
            </RecoveryPhraseContainer>
            <ButtonContainer>
                <PrimaryButton 
                    variant="outline"
                    onClick={() => navigate('/home')}
                >
                    {t('recoveryPhrase.display.backButton')}
                </PrimaryButton>
            </ButtonContainer>
        </>
    )
}

const ImportantNotes = ({
    onAgree
}: {
    onAgree: () => void
}) => {
    const [checkbox, setCheckbox] = useState(false)
    const { t } = useTranslation()
    return (
        <>
            <ImportantNotesContainer>
                <CircleBackgroundContainer>
                    <CircleBackground />
                </CircleBackgroundContainer>
                <Title>{t('recoveryPhrase.importantNotes.title')}</Title>

                <NotesContainer>
                    <NoteItem>
                        <IconContainer>
                            <DoNotShareIcon />
                        </IconContainer>
                        <NoteText>{t('recoveryPhrase.importantNotes.notes.doNotShare')}</NoteText>
                    </NoteItem>
                    
                    <NoteItem>
                        <IconContainer>
                            <WeNeverAskIcon />
                        </IconContainer>
                        <NoteText>{t('recoveryPhrase.importantNotes.notes.weNeverAsk')}</NoteText>
                    </NoteItem>

                    <NoteItem>
                        <IconContainer>
                            <MakeSureNoOneSeesIcon />
                        </IconContainer>
                        <NoteText>{t('recoveryPhrase.importantNotes.notes.privateSpace')}</NoteText>
                    </NoteItem>

                    <NoteItem>
                        <IconContainer>
                            <DoNotStoreInCloudIcon />
                        </IconContainer>
                        <NoteText>{t('recoveryPhrase.importantNotes.notes.doNotStore')}</NoteText>
                    </NoteItem>

                    <NoteItem>
                        <IconContainer>
                            <DoNotCopyIcon />
                        </IconContainer>
                        <NoteText>{t('recoveryPhrase.importantNotes.notes.doNotCopy')}</NoteText>
                    </NoteItem>
                </NotesContainer>


            </ImportantNotesContainer>
            <CheckboxContainer>
                <Checkbox 
                    label={t('recoveryPhrase.importantNotes.checkbox')}
                    checked={checkbox}
                    onChange={() => setCheckbox(prev => !prev)}
                />
                <PrimaryButton
                    disabled={!checkbox}
                    onClick={onAgree}
                >
                    {t('recoveryPhrase.importantNotes.viewRecoveryPhrase')}
                </PrimaryButton>
            </CheckboxContainer>            
        </>
    );
};

const CircleBackground = () => {
    return (
        <svg width="375" height="436" viewBox="0 0 375 436" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="187.627" cy="201.628" r="96.1728" stroke="#E6E9FF"/>
            <circle cx="187.627" cy="201.628" r="131.276" stroke="#E6E9FF"/>
            <circle cx="187.627" cy="201.627" r="177.26" stroke="#E6E9FF"/>
            <circle cx="187.627" cy="201.627" r="233.627" stroke="#E6E9FF"/>
        </svg>
    )
}

const DoNotShareIcon = () => {
    return (
        <svg width="17" height="15" viewBox="0 0 17 15" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M6.38491 2.80769C6.38491 1.80933 7.19425 1 8.19261 1C9.19097 1 10.0003 1.80933 10.0003 2.80769C10.0003 3.80605 9.19097 4.61538 8.19261 4.61538C7.19425 4.61538 6.38491 3.80605 6.38491 2.80769ZM8.19261 0C6.64196 0 5.38491 1.25705 5.38491 2.80769C5.38491 4.35834 6.64196 5.61538 8.19261 5.61538C9.74325 5.61538 11.0003 4.35834 11.0003 2.80769C11.0003 1.25705 9.74325 0 8.19261 0ZM1.76953 5.11538C1.76953 4.54186 2.23447 4.07692 2.80799 4.07692C3.38152 4.07692 3.84645 4.54186 3.84645 5.11538C3.84645 5.68891 3.38152 6.15385 2.80799 6.15385C2.23447 6.15385 1.76953 5.68891 1.76953 5.11538ZM2.40887 3.11598C1.47415 3.30152 0.769531 4.12617 0.769531 5.11538C0.769531 6.2412 1.68218 7.15385 2.80799 7.15385C3.79721 7.15385 4.62185 6.44922 4.8074 5.5145L3.90734 4.61444L2.40887 3.11598ZM6.54061 7.24771C5.67918 7.60194 4.95683 8.22489 4.47766 9.01181C4.01114 8.66618 3.43327 8.46131 2.80769 8.46131C1.25705 8.46131 0 9.71836 0 11.269V12.8075C0 13.0836 0.223858 13.3075 0.5 13.3075H12.6004L12.232 12.9391L11.6004 12.3075H1V11.269C1 10.2706 1.80933 9.46131 2.80769 9.46131C3.38326 9.46131 3.89584 9.72974 4.2277 10.1502C4.33828 10.2903 4.51515 10.3609 4.69179 10.3353C4.86843 10.3097 5.01806 10.192 5.08438 10.0263C5.4734 9.05441 6.30459 8.30828 7.32835 8.03545L6.54061 7.24771ZM13.0146 12.3075H15.3846V11.269C15.3846 10.2706 14.5753 9.46131 13.5769 9.46131C13.0014 9.46131 12.4888 9.72974 12.1569 10.1502C12.0463 10.2903 11.8695 10.3609 11.6928 10.3353C11.5162 10.3097 11.3666 10.192 11.3002 10.0263C10.8625 8.93261 9.86475 8.12481 8.66278 7.95567L7.66199 6.95488C7.83579 6.93374 8.01277 6.92285 8.19231 6.92285C9.76662 6.92285 11.1446 7.75978 11.907 9.01181C12.3735 8.66618 12.9513 8.46131 13.5769 8.46131C15.1276 8.46131 16.3846 9.71836 16.3846 11.269V12.8075C16.3846 13.0836 16.1608 13.3075 15.8846 13.3075H14.0146L13.0146 12.3075ZM12.5388 5.11538C12.5388 4.54186 13.0037 4.07692 13.5772 4.07692C14.1507 4.07692 14.6157 4.54186 14.6157 5.11538C14.6157 5.68891 14.1507 6.15385 13.5772 6.15385C13.0037 6.15385 12.5388 5.68891 12.5388 5.11538ZM13.5772 3.07692C12.4514 3.07692 11.5388 3.98957 11.5388 5.11538C11.5388 6.2412 12.4514 7.15385 13.5772 7.15385C14.703 7.15385 15.6157 6.24119 15.6157 5.11538C15.6157 3.98957 14.703 3.07692 13.5772 3.07692Z" fill="#3452FF"/>
            <path d="M1.5 0.5L4.26089 3.26089M15.3462 14.3462L12.5855 11.5855L10.0549 9.05486M10.0549 9.05486L6.79129 5.79129L4.26089 3.26089M10.0549 9.05486L4.26089 3.26089M9.63175 9.63201L3.83778 3.83804M3.83778 3.83804C5.03764 3.06449 6.46655 2.61561 8.0003 2.61561C11.4446 2.61561 14.3603 4.87939 15.3405 8.00025C14.7967 9.73155 13.6573 11.1991 12.1624 12.1627" stroke="#3452FF" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    )
}

const WeNeverAskIcon = () => {
    return (
        <svg width="16" height="14" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8.00003 5.69253V7.23099M8.00003 10.3079H8.00772M2.67064 13.3848H13.3294C14.5137 13.3848 15.2539 12.1028 14.6618 11.0771L9.33237 1.84638C8.74022 0.820738 7.25983 0.820738 6.66768 1.84638L1.33829 11.0771C0.746139 12.1028 1.48633 13.3848 2.67064 13.3848Z" stroke="#3452FF" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>

    )
}

const MakeSureNoOneSeesIcon = () => {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1.07689 1.07715L3.83778 3.83804M14.923 14.9233L12.1624 12.1627M9.44217 13.2499C8.97512 13.3385 8.49313 13.3848 8.00032 13.3848C4.55597 13.3848 1.64035 11.1211 0.660156 8.0002C0.927 7.15061 1.33728 6.36454 1.8626 5.67036M6.36818 6.36844C6.78579 5.95083 7.36271 5.69253 7.99996 5.69253C9.27446 5.69253 10.3077 6.72572 10.3077 8.00022C10.3077 8.63748 10.0494 9.2144 9.63175 9.63201M6.36818 6.36844L9.63175 9.63201M6.36818 6.36844L3.83778 3.83804M9.63175 9.63201L3.83778 3.83804M9.63175 9.63201L12.1624 12.1627M3.83778 3.83804C5.03764 3.06449 6.46655 2.61561 8.0003 2.61561C11.4446 2.61561 14.3603 4.87939 15.3405 8.00025C14.7967 9.73155 13.6573 11.1991 12.1624 12.1627" stroke="#3452FF" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    )
}

const DoNotStoreInCloudIcon = () => {
    return (
        <svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M4.41998 4.52146C4.80048 3.09158 6.10414 2.03809 7.65385 2.03809C9.27234 2.03809 10.6234 3.18767 10.9332 4.71513C10.9812 4.95183 11.1915 5.12039 11.433 5.11566C11.4553 5.11523 11.4776 5.11501 11.5 5.11501C13.348 5.11501 14.8462 6.61313 14.8462 8.46116C14.8462 10.0794 13.6969 11.4304 12.1698 11.7404C12.0345 11.7678 11.9232 11.8475 11.8525 11.954L12.5724 12.6739C14.454 12.1962 15.8462 10.4918 15.8462 8.46116C15.8462 6.16678 14.0683 4.28765 11.815 4.12626C11.2754 2.33938 9.61709 1.03809 7.65385 1.03809C5.83785 1.03809 4.28199 2.15187 3.63211 3.7336L4.30585 4.40734L4.41998 4.52146ZM4.34068 5.85638L3.59874 5.11444L3.33956 4.85526C3.31852 5.02864 3.30769 5.20518 3.30769 5.38424C3.30769 5.55004 3.317 5.71382 3.33514 5.87506C1.97188 6.37982 1 7.69113 1 9.23039C1 11.2059 2.60144 12.8073 4.57692 12.8073C4.85307 12.8073 5.07692 12.5835 5.07692 12.3073C5.07692 12.0312 4.85307 11.8073 4.57692 11.8073C3.15373 11.8073 2 10.6536 2 9.23039C2 8.00244 2.85938 6.97402 4.00998 6.71593C4.27934 6.65551 4.44877 6.38822 4.38847 6.11883C4.36917 6.03262 4.35319 5.94509 4.34068 5.85638ZM12.1759 12.045C12.2599 11.8951 12.3077 11.7224 12.3077 11.5384V6.4724C12.3077 6.40099 12.2793 6.33251 12.2288 6.28202L8.83329 2.88647C8.7828 2.83598 8.71432 2.80762 8.64291 2.80762H5.11538C4.54186 2.80762 4.07692 3.27255 4.07692 3.84608V3.94599L3.22151 3.09058C3.52168 2.3388 4.2565 1.80762 5.11538 1.80762H8.64291C8.97953 1.80762 9.30237 1.94134 9.54039 2.17937L12.9359 5.57492C13.174 5.81294 13.3077 6.13578 13.3077 6.4724V11.5384C13.3077 11.9993 13.1547 12.4244 12.8968 12.7659L12.8629 12.732L12.1759 12.045ZM2.5 5.88454H3.07692V11.5384C3.07692 12.6642 3.98957 13.5768 5.11538 13.5768H9.23077V14.1538C9.23077 14.9822 8.5592 15.6538 7.73077 15.6538H2.03846C1.46494 15.6538 1 15.1888 1 14.6153V7.38454C1 6.55611 1.67157 5.88454 2.5 5.88454Z" fill="#3452FF"/>
            <path d="M1.42383 1L4.18472 3.76089M15.27 14.8462L12.5094 12.0855L9.97869 9.55486M9.97869 9.55486L6.71512 6.29129L4.18472 3.76089M9.97869 9.55486L4.18472 3.76089" stroke="#3452FF" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    )
}

const DoNotCopyIcon = () => {
    return (
        <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M3.07692 4.3602V4.88454H2.5C1.11929 4.88454 0 6.00383 0 7.38454V14.6153C0 15.7411 0.91265 16.6538 2.03846 16.6538H7.73077C9.11148 16.6538 10.2308 15.5345 10.2308 14.1538V13.5768H11.2692C11.5697 13.5768 11.855 13.5118 12.1118 13.3951L11.2933 12.5766C11.2853 12.5768 11.2773 12.5768 11.2692 12.5768H9.73077H5.11538C4.54186 12.5768 4.07692 12.1119 4.07692 11.5384V5.38454V5.3602L3.83117 5.11444L3.07692 4.3602ZM12.1759 12.045C12.2599 11.8951 12.3077 11.7224 12.3077 11.5384V6.4724C12.3077 6.40099 12.2793 6.33251 12.2288 6.28202L8.83329 2.88647C8.7828 2.83598 8.71432 2.80762 8.64291 2.80762H5.11538C4.54186 2.80762 4.07692 3.27255 4.07692 3.84608V3.94599L3.22151 3.09058C3.52168 2.3388 4.2565 1.80762 5.11538 1.80762H8.64291C8.97953 1.80762 9.30237 1.94134 9.54039 2.17937L12.9359 5.57492C13.174 5.81294 13.3077 6.13578 13.3077 6.4724V11.5384C13.3077 11.9993 13.1547 12.4244 12.8968 12.7659L12.8629 12.732L12.1759 12.045ZM2.5 5.88454H3.07692V11.5384C3.07692 12.6642 3.98957 13.5768 5.11538 13.5768H9.23077V14.1538C9.23077 14.9822 8.5592 15.6538 7.73077 15.6538H2.03846C1.46494 15.6538 1 15.1888 1 14.6153V7.38454C1 6.55611 1.67157 5.88454 2.5 5.88454Z" fill="#3452FF"/>
            <path d="M1.42383 1L4.18472 3.76089M15.27 14.8462L12.5094 12.0855L9.97869 9.55486M9.97869 9.55486L6.71512 6.29129L4.18472 3.76089M9.97869 9.55486L4.18472 3.76089" stroke="#3452FF" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    )
}

export default ShowRecoveryPhrase;
